import { Fragment } from "react";
import classNames from "classnames";
import Image from "components/imageNew/image";
import TextButton from "components/textButton/TextButtonLegacy";
import {
  EmailButton,
  LinkedInButton,
  SignalButton,
  TwitterButton,
} from "components/socialButtons/socialButtons";
import type { TeamMemberPitch } from "../types";
import { useMixpanelTracking } from "lib/hooks/useMixpanelTracking";
import { imageLoader } from "utils";

type TeamMemberCardProps = {
  member: TeamMemberPitch;
};

export const TeamMemberCard = ({ member }: TeamMemberCardProps) => {
  const { track } = useMixpanelTracking();
  return (
    <div className="flex gap-x-4">
      {member.image?.url && (
        <div className="h-[90px] w-[86px] lg:h-[130px] lg:w-[124px]">
          <Image
            className={classNames(
              "[clip-path:_polygon(11%_0,_100%_0,_100%_20%,_100%_80%,_92%_100%,_0_100%,_0%_80%,_0_13%)]",
              "h-100 w-100 object-cover"
            )}
            loader={imageLoader}
            src={member.image?.url}
            alt={`${member.name} portrait`}
            height={member.image?.height}
            width={member.image?.width}
            sizes="(min-width: 768px) 200px, (min-width: 1200px) 400px, 400px"
          />
        </div>
      )}
      <div className="flex flex-1 flex-col">
        <p className="font-bold text-[16px]">{member.name}</p>

        {member.topicsCovered && (
          <div className="mt-[6px]">
            {member.topicsCovered.map((topic, i) => {
              return (
                <Fragment key={i}>
                  {topic.topicTitle}
                  {member.topicsCovered && member.topicsCovered.length > i + 1
                    ? ", "
                    : null}
                </Fragment>
              );
            })}
          </div>
        )}

        <div className="mt-[18px] flex w-100 flex-col-reverse lg:mt-auto lg:flex-row lg:items-center lg:justify-between">
          <div className="mt-4 flex items-center gap-x-5 lg:mt-0 lg:justify-end">
            <SignalButton
              signalNumber={member.signal}
              onClick={async () => {
                await track("Team member card click", {
                  "Team member": member.name,
                  Type: "Signal",
                });
                navigator.clipboard.writeText(`${member.signal}`);
              }}
            />
            <EmailButton
              href={`mailto:${member.email}`}
              onClick={async () => {
                await track("Team member card click", {
                  "Team member": member.name,
                  Type: "Email",
                });
              }}
            />
            <TwitterButton
              href={member.twitter}
              onClick={async () => {
                await track("Team member card click", {
                  "Team member": member.name,
                  Type: "Twitter",
                  "Click link": member.twitter,
                });
              }}
            />
            <LinkedInButton
              href={member.linkedin}
              onClick={async () => {
                await track("Team member card click", {
                  "Team member": member.name,
                  Type: "LinkedIn",
                  "Click link": member.linkedin,
                });
              }}
            />
          </div>
          <TextButton
            href={member.articlesLink}
            className={"w-min-content whitespace-nowrap"}
            withIcon={true}
            labelIcon={"See Articles"}
            onClick={async () => {
              await track("Pitch your story page click", {
                "Click link": member.articlesLink,
                "Click text": " See articles",
              });
            }}
          >
            See articles
          </TextButton>
        </div>
      </div>
    </div>
  );
};
